import React from 'react';
import { Form, Select, Transfer, Table } from 'antd';
import { difference } from 'lodash';
import { useAppliedProperties } from './hooks';

const AppliedProperties = props => {
  const {
    columns,
    bookingJourneys,
    isLoading,
    properties,
    pageSize,
    selectedItems,
    currency,
    pageNumber,
    pageSizeRight,
    pageNumberRight,
    totalCount,
    disabledCurrency,
    t,
    handleSelectCurrency,
    handleSearch,
    getProperties,
    setPageNumber,
    setPageSize,
    setPageNumberRight,
    setPageSizeRight,
    setSelectedItems,
    setIsTouched,
    setSelectedItemSnapshot,
  } = useAppliedProperties(props);

  const { form, currencyOptions, landlordId } = props;
  const { getFieldDecorator } = form;

  return (
    <div className="applied-properties">
      <div className="applied-properties__currency">
        <Form.Item
          className="applied-properties__currency-title"
          label={t('landlord:landlord.payment.long_tail.initial_setup.label.currency')}
          colon={false}
        >
          {getFieldDecorator('currency', {
            rules: [
              {
                required: true,
              },
            ],
          })(
            <Select
              placeholder={t('landlord:landlord.payment.long_tail.currency.select.placeholder')}
              onChange={value => {
                handleSelectCurrency(value, false);
              }}
              disabled={disabledCurrency}
            >
              {currencyOptions?.length === 0 && (
                <Select.Option value="" disabled>
                  {t('landlord.payment.long_tail.currency.select.no_options')}
                </Select.Option>
              )}
              {currencyOptions?.map(currency => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>,
          )}
        </Form.Item>
      </div>
      <div
        className={`applied-properties__properties ${
          !currency ? 'applied-properties__properties--hidden' : ''
        }`}
      >
        <Form.Item label={t('landlord:landlord.payment.long_tail.applied_properties.field')}>
          {getFieldDecorator('properties', {
            initialValue: selectedItems,
          })(
            <div>
              <div className="applied-properties__hint">
                {t(
                  'landlord:landlord.payment.long_tail.initial_setup.label.applied_properties_hint',
                )}
              </div>
              <Transfer
                dataSource={properties}
                targetKeys={selectedItems.map(item => item.id)}
                rowKey={record => record.id}
                onChange={(newSelectedKeys, direction, moveKeys) => {
                  setIsTouched(true);
                  let newSelectedItems = [];
                  if (direction === 'left') {
                    newSelectedItems = selectedItems.filter(item => {
                      return !moveKeys.includes(item.id);
                    });
                  } else {
                    newSelectedItems = selectedItems.concat(
                      properties.filter(item => moveKeys.includes(item.id)),
                    );
                  }
                  setSelectedItems(newSelectedItems);
                  setSelectedItemSnapshot(newSelectedItems);
                }}
                showSearch
                showSelectAll={false}
                filterOption={() => {
                  return true;
                }}
                onSearch={handleSearch}
              >
                {({ direction, onItemSelect, onItemSelectAll, selectedKeys: listSelectedKeys }) => {
                  const rowSelection = {
                    getCheckboxProps: item => ({
                      disabled:
                        (direction === 'left' &&
                          (item.disabled ||
                            selectedItems.some(selected => selected.id === item.id))) ||
                        (direction === 'right' && item.disabled),
                    }),
                    onSelectAll: (selected, selectedRows) => {
                      const treeSelectedKeys = selectedRows
                        .filter(item => !item.disabled)
                        .map(({ id }) => id);
                      const diffKeys = selected
                        ? difference(treeSelectedKeys, listSelectedKeys)
                        : difference(listSelectedKeys, treeSelectedKeys);
                      onItemSelectAll(diffKeys, selected);
                    },
                    onSelect({ id }, selected) {
                      onItemSelect(id, selected);
                    },
                    selectedRowKeys: listSelectedKeys,
                  };

                  const handleTableChange = paginationObj => {
                    if (direction === 'left') {
                      setPageNumber(paginationObj.current);
                      setPageSize(paginationObj.pageSize);
                      const variables = {
                        currency,
                        pageNumber: paginationObj.current,
                        pageSize: paginationObj.pageSize,
                        bookingJourneys,
                      };
                      if (landlordId) {
                        variables.landlordIds = [landlordId];
                      }
                      getProperties({ variables });
                    }
                  };
                  return (
                    <Table
                      rowSelection={rowSelection}
                      rowClassName={record => (record.disabled ? 'table-row-disabled' : '')}
                      columns={columns}
                      loading={isLoading}
                      scroll={{ y: 300 }}
                      dataSource={direction === 'left' ? properties : selectedItems}
                      size="small"
                      rowKey="id"
                      onRow={({ id, disabled: itemDisabled }) => ({
                        onClick: () => {
                          if (
                            itemDisabled ||
                            (direction === 'left' &&
                              selectedItems.some(selected => selected.id === id))
                          )
                            return;
                          onItemSelect(id, !listSelectedKeys.includes(id));
                        },
                      })}
                      onChange={handleTableChange}
                      pagination={
                        direction === 'left'
                          ? {
                              current: pageNumber,
                              pageSize,
                              total: totalCount,
                              showSizeChanger: true,
                            }
                          : {
                              current: pageNumberRight,
                              pageSize: pageSizeRight,
                              total: selectedItems.length,
                              showSizeChanger: true,
                              onShowSizeChange: (current, size) => {
                                setPageNumberRight(1);
                                setPageSizeRight(size);
                              },
                              onChange: (page, size) => {
                                setPageNumberRight(page);
                                setPageSizeRight(size);
                              },
                            }
                      }
                    />
                  );
                }}
              </Transfer>
            </div>,
          )}
        </Form.Item>
      </div>
    </div>
  );
};

export default AppliedProperties;
